import React, { useState } from "react";
import { api } from "../api";
import { session } from "../helpers";
import { Icon, PasswordInput, OtpInputs, useToast } from "../components/ui";

export function Login({ onLogin }) {
  const toast = useToast();
  const [mode, setMode] = useState("login"); // login | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);   // "Remember me" ticked by default
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // reset flow state
  const [resetEmail, setResetEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const res = await api.login(email.trim(), password);
      session.setRememberedEmail(remember ? email.trim() : "");
      onLogin(res, remember);
    } catch (ex) { setErr(ex.message || "Sign in failed."); }
    finally { setBusy(false); }
  };

  const sendResetCode = async (e) => {
    e.preventDefault();
    setErr("");
    if (!resetEmail.trim()) { setErr("Enter your work email."); return; }
    setBusy(true);
    try {
      await api.forgotPassword(resetEmail.trim());
      toast("Reset code emailed to " + resetEmail.trim(), "info");
      setMode("reset");
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setErr("");
    if (code.length < 6) { setErr("Enter the 6-digit code from the email."); return; }
    if (newPw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (newPw !== newPw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.resetPassword(resetEmail.trim(), code, newPw);
      toast("Password updated — please sign in", "ok");
      setPassword(""); setCode(""); setNewPw(""); setNewPw2("");
      setEmail(resetEmail.trim());
      setMode("login");
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  const goForgot = () => { setResetEmail(email || ""); setErr(""); setMode("forgot"); };
  const backToLogin = () => { setErr(""); setMode("login"); };

  return (
    <div className="login login-bg" style={{ backgroundImage: "url(/images/login.png)" }}>
      <div className="login-spacer" />
      <main className="login-form-side">
        <div className="login-card nocard">

          {mode === "login" && (
            <form onSubmit={submit} autoComplete="on">
              <h1>Welcome back</h1>
              <p className="lc-sub">Sign in to your console.</p>

              <div className="field">
                <label htmlFor="email">Work email</label>
                <input id="email" name="username" type="email" className="inp" autoComplete="username"
                  placeholder="you@practo-priorauth.local" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="field">
                <label htmlFor="password">Password</label>
                <PasswordInput id="password" name="password" autoComplete="current-password"
                  placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>

              <div className="login-row">
                <label className="check">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                  <span className="box">{remember && <Icon.check />}</span>Remember me
                </label>
                <button type="button" className="link-btn" onClick={goForgot}>Forgot password?</button>
              </div>

              {err && <div className="err-text" role="alert" style={{ marginBottom: 14 }}>{err}</div>}
              <button type="submit" className="btn btn-primary btn-block" disabled={busy}>{busy ? <span className="spin" /> : "Sign in"}</button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={sendResetCode}>
              <h1>Reset password</h1>
              <p className="lc-sub">Enter your email and we'll send a verification code.</p>
              <div className="field">
                <label htmlFor="remail">Work email</label>
                <input id="remail" type="email" className="inp" autoComplete="username"
                  placeholder="you@practo-priorauth.local" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} required />
              </div>
              {err && <div className="err-text" style={{ marginBottom: 14 }}>{err}</div>}
              <button type="submit" className="btn btn-primary btn-block" disabled={busy}>{busy ? <span className="spin" /> : "Send code"}</button>
              <button type="button" className="link-btn" style={{ marginTop: 16, display: "block" }} onClick={backToLogin}>← Back to sign in</button>
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={doReset} autoComplete="on">
              <input type="text" name="username" autoComplete="username" value={resetEmail} readOnly hidden />
              <h1>Enter code &amp; new password</h1>
              <p className="lc-sub">We emailed a 6-digit code to <b>{resetEmail}</b>.</p>
              <OtpInputs value={code} onChange={setCode} />
              <div className="field" style={{ marginTop: 16 }}>
                <label htmlFor="npw">New password</label>
                <PasswordInput id="npw" name="new-password" autoComplete="new-password"
                  placeholder="At least 8 characters" value={newPw} onChange={(e) => setNewPw(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="npw2">Confirm new password</label>
                <PasswordInput id="npw2" name="new-password" autoComplete="new-password"
                  placeholder="Re-enter new password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} />
              </div>
              {err && <div className="err-text" style={{ marginBottom: 14 }}>{err}</div>}
              <button type="submit" className="btn btn-primary btn-block" disabled={busy}>{busy ? <span className="spin" /> : "Reset password"}</button>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                <button type="button" className="link-btn" onClick={() => setMode("forgot")}>← Change email</button>
                <button type="button" className="link-btn" onClick={sendResetCode}>Resend code</button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}